import {Injectable, NgZone} from '@angular/core';
import {Stopwatch} from "../lib/stopwatch";
import {Subject} from "rxjs";
import {Mime} from "scribe-poc-shared";
import {Capacitor} from "@capacitor/core";
import {CapacitorAudioRecorder, RecordingErrorEvent, StopRecordingResult} from "@capgo/capacitor-audio-recorder";
import {Utils} from "../lib/utils";
import {RecordingToUpload} from "../lib/recording-to-upload";

/** Handles interaction with the user's microphone for recording audio */
@Injectable({
	providedIn: 'root'
})
export class RecordingService {
	/** Duration of a recording being made */
	private recordingStopwatch:Stopwatch;

	/** The preferred mime type of the audio that this browser wants to record in */
	private _preferredMime:string;

	/** Events issued when there is an error recording */
	private onErrorSubject = new Subject<RecordingErrorEvent>();

	/** Events issued when the mic is interrupted (phone call or music playing) */
	private onInterruptedSubject = new Subject<void>();

	/** Interruption monitoring audio context */
	private audioContext:AudioContext;

	/** Wake lock monitoring for mobile */
	private wakeLock:WakeLockSentinel;

	/** Are we currently recording? */
	private recordingState:RecordingStatus = RecordingStatus.Inactive;

	constructor(private ngZone:NgZone) {
	}

	/** Attempt to determine support of a microphone, this will also get the initial microphone devices enabled */
	async testSupport() {
		//attempt to get the devices, by starting the audio system and closing it quick
		if(this.isSupported) {
			//check permissions
			const current = await CapacitorAudioRecorder.checkPermissions();
			if(current.recordAudio == "granted") {
				return true;
			}

			//if permission not granted then ask for it
			const permission = await CapacitorAudioRecorder.requestPermissions();
			if(permission.recordAudio == "granted") {
				return true;
			}
		}

		return false;
	}

	/** Start recording the user */
	async startMic() {
		try {
			await CapacitorAudioRecorder.startRecording();

			//listen for errors
			await CapacitorAudioRecorder.addListener("recordingError", event => {
				console.error("An error occurred while recording", event);
				this.ngZone.run(() => {
					this.onErrorSubject.next(event);
				});
			});

			//listen for interruptions (phone calls, music playing, etc)
			this.audioContext = new AudioContext();
			this.audioContext.onstatechange = () => {
				if(this.audioContext.state as string == "interrupted") {
					console.info("Audio stream was interrupted externally");
					this.ngZone.run(() => {
						this.onInterruptedSubject.next();
					});
				}
			};

			await this.setWakeLock(true);

			//start timing
			this.recordingStopwatch = new Stopwatch();
			this.recordingStopwatch.start();

			this.recordingState = RecordingStatus.Recording;

			return true;
		} catch(e) {
			console.error("Failed to start mic", e);
			return false;
		}
	}

	/** Pause the recording */
	pauseMic() {
		if(this.recordingState == RecordingStatus.Recording) {
			CapacitorAudioRecorder.pauseRecording();
			this.recordingStopwatch.stop();
			this.recordingState = RecordingStatus.Paused;
		}
	}

	/** Resume the recording from a pause */
	resumeMic() {
		if(this.recordingState == RecordingStatus.Paused) {
			CapacitorAudioRecorder.resumeRecording();
			this.recordingStopwatch.start();
			this.recordingState = RecordingStatus.Recording;
		}
	}

	/** Cancel recording and close the stream, the recording will be discarded */
	cancelMic() {
		this.doStop(true);
	}

	/** Stop recording and close the stream */
	async stopMic() {
		return await this.doStop(false) as RecordingToUpload;
	}

	/**
	 * Stop recording and close the stream
	 * @param isCancel if true the audio recording was canceled, throw away the result
	 */
	private async doStop(isCancel:boolean):Promise<void | RecordingToUpload> {
		if(this.recordingState == RecordingStatus.Inactive) {
			console.error("Can't stop a stream that is inactive");
			return;
		}

		let result:StopRecordingResult;

		if(isCancel) {
			await CapacitorAudioRecorder.cancelRecording()
		} else {
			result = await CapacitorAudioRecorder.stopRecording();
		}

		await CapacitorAudioRecorder.removeAllListeners();

		this.recordingState = RecordingStatus.Inactive;

		//stop listening to interruptions
		if(this.audioContext) {
			this.audioContext.onstatechange = null;
			this.audioContext.close().then(() => {
				this.audioContext = null;
			});
		}

		//release the wake lock if we can
		this.setWakeLock(false).then();

		this.recordingStopwatch?.stop();

		//wait for the angular zone and then finish up
		await Utils.waitForAngularZone(this.ngZone);

		if(isCancel) {
			return;
		}

		result.duration = this.recordingStopwatch.duration;

		const recording = new RecordingToUpload();
		await recording.save(result);
		return recording;
	}

	/** A list of file extensions accepted for file uploads */
	static get mimesAccepted():string {
		return Mime.supportedExtensions.map(value => `.${value}`).join(",");
	}

	/**
	 * Query the browser and get the first supported mime type, from a list of supported types
	 * @see Mime.supportedMimes
	 */
	private getFirstSupportedMime():boolean {
		for(const mime of Mime.supportedMimes) {
			if(MediaRecorder.isTypeSupported(mime)) {
				this._preferredMime = mime;
				return true;
			}
		}

		return false;
	}

	/**
	 * Enable or disable the wake lock or always on screen. This will not do anything if the platform doesn't support it,
	 * or we're on a mobile device
	 * @param enabled whether to enable or disable to wake lock
	 */
	private async setWakeLock(enabled:boolean) {
		if(navigator.wakeLock && !Capacitor.isNativePlatform()) {
			try {
				if(enabled) {
					this.wakeLock = await navigator.wakeLock.request("screen");
				} else {
					await this.wakeLock?.release();
					this.wakeLock = null;
				}
			} catch(e) {
				console.warn("Failed to grab the wake lock", e);
			}
		}
	}

	/** Return the total time of the recording in milliseconds */
	get recordingTime():number {
		//prioritize the stop watch, then the final duration, then 0
		return this.recordingStopwatch?.duration ?? 0;
	}

	/** The preferred mime type of the audio that this browser wants to record in */
	get preferredMime():string {
		return this._preferredMime;
	}

	/** Events issued when there is an error recording */
	get onErrorEvent() {
		return this.onErrorSubject.asObservable();
	}

	/** Events issued when the mic stops recording */
	get onInterruptedEvent() {
		return this.onInterruptedSubject.asObservable();
	}

	/** Is mic recording supported by the browser / device? */
	private get isSupported():boolean {
		return navigator.mediaDevices &&
			typeof navigator.mediaDevices.getUserMedia == "function" &&
			this.getFirstSupportedMime();
	}
}

/** State of the recording process */
enum RecordingStatus {
	/** Waiting for the recording to start */
	Inactive,

	/** Microphone running */
	Recording,

	/** Paused, but not done */
	Paused
}
